import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, Check, User, CalendarClock, MapPin, Users, Car,
  DollarSign, ClipboardCheck, Search, Plane, Loader2, X,
} from 'lucide-react';
import './os.css';
import { StatusBadge, ConfirmModal } from './osPrimitives';

const API = process.env.REACT_APP_BACKEND_URL;
const money = (n) => (n === undefined || n === null || n === '') ? '—' : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const authHeaders = () => {
  const token = localStorage.getItem('ace_admin_token');
  return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
};
async function getJSON(path) {
  const r = await fetch(`${API}${path}`, { credentials: 'include', headers: authHeaders() });
  if (!r.ok) throw new Error(`${path} ${r.status}`);
  return r.json();
}
async function postJSON(path, body) {
  const r = await fetch(`${API}${path}`, { method: 'POST', credentials: 'include', headers: authHeaders(), body: JSON.stringify(body) });
  if (!r.ok) { const t = await r.text().catch(() => ''); throw new Error(t || `${path} ${r.status}`); }
  return r.json();
}

const SERVICE_TYPES = [
  { id: 'Hourly', label: 'Hourly / As-Directed' },
  { id: 'Point-to-Point', label: 'Point-to-Point' },
  { id: 'Airport Transfer', label: 'Airport Transfer', flat: true },
  { id: 'Event', label: 'Event / Occasion' },
  { id: 'Corporate', label: 'Corporate' },
  { id: 'Wedding', label: 'Wedding' },
  { id: 'Production', label: 'Production' },
];
const PERIMETERS = [
  { id: 'inside', label: 'Inside Perimeter (I-285)', fee: 100, note: 'Atlanta · Midtown · Buckhead · Downtown' },
  { id: 'outside', label: 'Outside Perimeter', fee: 150, note: 'Alpharetta · Marietta · Sandy Springs · suburbs' },
];

const STEPS = [
  { id: 'client', label: 'Client', icon: User },
  { id: 'service', label: 'Service', icon: ClipboardCheck },
  { id: 'when', label: 'When', icon: CalendarClock },
  { id: 'route', label: 'Route', icon: MapPin },
  { id: 'passengers', label: 'Trip', icon: Users },
  { id: 'vehicle', label: 'Vehicle', icon: Car },
  { id: 'price', label: 'Price', icon: DollarSign },
  { id: 'review', label: 'Review', icon: Check },
];

const EMPTY = {
  client_name: '', client_phone: '', client_email: '',
  service_type: 'Hourly', perimeter: 'inside',
  event_date: '', pickup_time: '', dropoff_time: '',
  pickup_location: '', dropoff_location: '', flight_number: '',
  passengers: '', hours: '3',
  vehicle_type: '', vehicle_name: '', pricing_type: 'retail',
  override_price: false, override_total: '',
  notes: '',
};

const Field = ({ label, children, hint }) => (
  <label className="block mb-4">
    <span className="text-[11px] uppercase tracking-wider font-bold" style={{ color: 'var(--os-muted)' }}>{label}</span>
    <div className="mt-1.5">{children}</div>
    {hint && <p className="text-[11px] mt-1" style={{ color: 'var(--os-muted)' }}>{hint}</p>}
  </label>
);

export default function OSSmartServiceForm() {
  const navigate = useNavigate();
  const { bookingId, quoteId } = useParams();
  const quoteMode = !!quoteId || /\/services\/quotes(\/|$)/.test(window.location.pathname);
  const editMode = !!bookingId || !!quoteId;
  const [inquiryId, setInquiryId] = useState('');
  const [original, setOriginal] = useState(null);
  const [diffOpen, setDiffOpen] = useState(false);
  const [blocked, setBlocked] = useState(null);
  const [stepIdx, setStepIdx] = useState(0);
  const [form, setForm] = useState(EMPTY);
  const [dirty, setDirty] = useState(false);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [pendingLeave, setPendingLeave] = useState(null);

  // Preselect client from deep-link (?client_name/phone/email + optional quote prefill)
  useEffect(() => {
    if (editMode) return;
    const sp = new URLSearchParams(window.location.search);
    const cn = sp.get('client_name'), cp = sp.get('client_phone'), ce = sp.get('client_email');
    const st = sp.get('service_type'), ed = sp.get('event_date'), iq = sp.get('inquiry_id');
    if (iq) setInquiryId(iq);
    if (cn || cp || ce || st || ed) setForm((f) => ({
      ...f,
      ...(cn || cp || ce ? { client_name: cn || '', client_phone: cp || '', client_email: ce || '' } : {}),
      ...(st ? { service_type: st } : {}),
      ...(ed ? { event_date: ed } : {}),
    }));
  }, []);

  // Smart Quote Edit: preload the canonical quote; block converted quotes
  useEffect(() => {
    if (!quoteId) return;
    getJSON('/api/command-center/quote-log?include_deleted=true').then((d) => {
      const qt = (d.quotes || []).find((x) => x.quote_id === quoteId);
      if (!qt) { setBlocked({ reason: 'Quote not found.' }); return; }
      if (qt.booking_id || ['confirmed', 'converted'].includes((qt.status || '').toLowerCase())) {
        setBlocked({ reason: 'This quote has been converted to a booking and can no longer be edited here.', booking: qt.booking_id ? { booking_id: qt.booking_id } : null });
        return;
      }
      setInquiryId(qt.inquiry_id || '');
      setForm((f) => ({
        ...f,
        client_name: qt.client_name || '', client_phone: qt.client_phone || '', client_email: qt.client_email || '',
        service_type: qt.service_type || 'Hourly',
        event_date: (qt.event_date || '').slice(0, 10),
        pickup_location: qt.pickup_location || '', dropoff_location: qt.dropoff_location || '',
        passengers: qt.passengers != null ? String(qt.passengers) : '',
        hours: qt.hours != null ? String(qt.hours) : '3',
        vehicle_type: qt.vehicle_type || '', vehicle_name: qt.vehicle_name || qt.vehicle || '',
        pricing_type: qt.pricing_type || 'retail',
        notes: (qt.notes || '').split(' · Custom price override')[0],
      }));
      setOriginal(qt);
    }).catch(() => setBlocked({ reason: 'Could not load quote.' }));
  }, [quoteId]);

  // Smart Edit: preload the canonical booking; gate finalized states client-side
  useEffect(() => {
    if (!bookingId) return;
    getJSON('/api/command-center/confirmed-bookings').then((d) => {
      const b = (d.bookings || []).find((x) => x.booking_id === bookingId);
      if (!b) { setBlocked({ reason: 'Booking not found.' }); return; }
      const finalized = ['completed', 'cancelled', 'canceled', 'deleted', 'finalized'].includes((b.cc_status || '').toLowerCase());
      if (finalized) { setBlocked({ reason: `This booking is ${b.cc_status} and can't be edited here. Use the Advanced Tool.`, booking: b }); return; }
      setForm((f) => ({
        ...f,
        client_name: b.client_name || '', client_phone: b.client_phone || '', client_email: b.client_email || '',
        service_type: b.service_type || 'Hourly',
        event_date: (b.event_date || '').slice(0, 10), pickup_time: b.pickup_time || '', dropoff_time: b.dropoff_time || '',
        pickup_location: b.pickup_location || '', dropoff_location: b.dropoff_location || '', flight_number: b.flight_number || '',
        passengers: b.passengers != null ? String(b.passengers) : '', hours: b.hours != null ? String(b.hours) : '3',
        vehicle_type: b.vehicle_type || '', vehicle_name: b.vehicle_name || b.vehicle || '',
        pricing_type: b.pricing_type || 'retail', notes: (b.notes || '').split(' · Custom price override')[0],
      }));
      setOriginal(b);
    }).catch(() => setBlocked({ reason: 'Could not load booking.' }));
  }, [editMode, bookingId]);

  const [rateCard, setRateCard] = useState({});
  const [clients, setClients] = useState([]);
  const [clientQuery, setClientQuery] = useState('');
  const [settings, setSettings] = useState({ default_gratuity_pct: 12, default_sales_tax_pct: 8 });

  const [pricing, setPricing] = useState(null);
  const [pricingLoading, setPricingLoading] = useState(false);
  const [pricingErr, setPricingErr] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null); // { kind, id }

  const step = STEPS[stepIdx];
  const isFlat = SERVICE_TYPES.find((s) => s.id === form.service_type)?.flat;

  const set = useCallback((patch) => { setForm((f) => ({ ...f, ...patch })); setDirty(true); }, []);

  // Load reference data
  useEffect(() => {
    getJSON('/api/pricing/quote/rate-card').then(setRateCard).catch(() => setRateCard({}));
    getJSON('/api/command-center/ops/settings').then((s) => setSettings(s || {})).catch(() => {});
  }, []);

  // Client typeahead (debounced)
  useEffect(() => {
    const q = clientQuery.trim();
    if (q.length < 2) { setClients([]); return undefined; }
    const t = setTimeout(() => {
      getJSON(`/api/command-center/clients?search=${encodeURIComponent(q)}`)
        .then((d) => setClients((d.clients || []).slice(0, 6))).catch(() => setClients([]));
    }, 260);
    return () => clearTimeout(t);
  }, [clientQuery]);

  // Warn on tab close while dirty
  useEffect(() => {
    const h = (e) => { if (dirty && !done) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [dirty, done]);

  // ---- Authoritative pricing (called when entering Price step) ----
  const runPricing = useCallback(async () => {
    if (isFlat) {
      const per = PERIMETERS.find((p) => p.id === form.perimeter) || PERIMETERS[0];
      setPricing({ base: per.fee, applied_rate: null, effective_hours: 1, vehicle: form.vehicle_name, flat: true, label: per.label });
      setPricingErr(false); return;
    }
    if (!form.vehicle_type) { setPricing(null); return; }
    setPricingLoading(true); setPricingErr(false);
    try {
      const res = await postJSON('/api/pricing/quote/calculate', {
        vehicle_type: form.vehicle_type,
        hours: Number(form.hours) || 0,
        pricing_type: form.pricing_type,
      });
      setPricing({ base: res.revenue, applied_rate: res.applied_rate, effective_hours: res.effective_hours, vehicle: res.vehicle });
    } catch { setPricingErr(true); setPricing(null); }
    finally { setPricingLoading(false); }
  }, [isFlat, form.perimeter, form.vehicle_type, form.hours, form.pricing_type, form.vehicle_name]);

  useEffect(() => { if (step.id === 'price') runPricing(); }, [step.id, runPricing]);

  const gratPct = Number(settings.default_gratuity_pct ?? 12);
  const taxPct = Number(settings.default_sales_tax_pct ?? 8);
  const totals = useMemo(() => {
    const base = pricing ? Number(pricing.base || 0) : 0;
    const gratuity = +(base * (gratPct / 100)).toFixed(2);
    const sales_tax = +(base * (taxPct / 100)).toFixed(2);
    const computed = +(base + gratuity + sales_tax).toFixed(2);
    const total = form.override_price && form.override_total !== '' ? Number(form.override_total) : computed;
    return { base, gratuity, sales_tax, computed, total };
  }, [pricing, gratPct, taxPct, form.override_price, form.override_total]);

  // ---- Step validation (only true boundaries; keep it light) ----
  const stepValid = useMemo(() => {
    switch (step.id) {
      case 'client': return !!(form.client_name || form.client_phone || form.client_email);
      case 'when': return !!form.event_date;
      case 'vehicle': return isFlat ? true : !!form.vehicle_type;
      case 'price': return !!pricing && !pricingLoading && (!form.override_price || form.override_total !== '');
      default: return true;
    }
  }, [step.id, form, isFlat, pricing, pricingLoading]);

  const next = () => setStepIdx((i) => Math.min(i + 1, STEPS.length - 1));
  const back = () => setStepIdx((i) => Math.max(i - 1, 0));

  const requestLeave = (to) => { if (dirty && !done) { setPendingLeave(to); setLeaveOpen(true); } else navigate(to); };

  // ---- Payload builders ----
  const bookingPayload = () => ({
    client_name: form.client_name, client_phone: form.client_phone, client_email: form.client_email,
    service_type: form.service_type,
    event_date: form.event_date, pickupDate: form.event_date,
    pickup_time: form.pickup_time, dropoff_time: form.dropoff_time,
    pickup_location: form.pickup_location, dropoff_location: form.dropoff_location,
    flight_number: form.flight_number,
    passengers: Number(form.passengers) || 0,
    hours: isFlat ? 0 : (Number(form.hours) || 0),
    vehicle: form.vehicle_name, vehicle_name: form.vehicle_name, vehicle_type: form.vehicle_type,
    pricing_type: form.pricing_type,
    base_total: totals.base, gratuity: totals.gratuity, sales_tax: totals.sales_tax, total: totals.total,
    notes: [form.notes, isFlat ? `Airport · ${pricing?.label || form.perimeter}` : '', form.override_price ? 'Custom price override' : '']
      .filter(Boolean).join(' · '),
    cc_status: editMode && original ? (original.cc_status || 'upcoming') : 'upcoming',
  });
  const quotePayload = () => ({
    ...(inquiryId || original?.inquiry_id ? { inquiry_id: inquiryId || original?.inquiry_id } : {}),
    client_name: form.client_name, client_phone: form.client_phone, client_email: form.client_email,
    service_type: form.service_type, event_date: form.event_date,
    pickup_location: form.pickup_location, dropoff_location: form.dropoff_location,
    passengers: Number(form.passengers) || 0,
    vehicle_name: form.vehicle_name, vehicle_type: form.vehicle_type, pricing_type: form.pricing_type,
    hours: isFlat ? 0 : (Number(form.hours) || 0),
    base_total: totals.base, gratuity: totals.gratuity, sales_tax: totals.sales_tax,
    quoted_amount: totals.total, grand_total: totals.total, total: totals.total,
    notes: [form.notes, isFlat ? `Airport · ${pricing?.label || form.perimeter}` : '', form.override_price ? 'Custom price override' : ''].filter(Boolean).join(' · '),
    status: editMode && original ? (original.status || 'sent') : 'sent',
  });
  const leadPayload = () => ({
    source: 'os-smart-form',
    client_name: form.client_name, client_phone: form.client_phone, client_email: form.client_email,
    service_type: form.service_type, event_date: form.event_date,
    message: `Smart-form lead · ${form.vehicle_name || form.service_type}`,
    pickup_time: form.pickup_time, vehicle: form.vehicle_name, hours: Number(form.hours) || 0,
    quoted_amount: totals.total, notes: form.notes,
  });

  const submit = async (kind) => {
    setSubmitting(true);
    try {
      if (kind === 'booking') { const r = await postJSON('/api/command-center/confirmed-bookings', bookingPayload()); setDone({ kind: 'Booking', id: r.booking_id }); }
      else if (kind === 'quote') { const r = await postJSON('/api/command-center/quote-log', quotePayload()); setDone({ kind: 'Quote', id: r.quote_id }); }
      else { const r = await postJSON('/api/ops/inquiries', leadPayload()); setDone({ kind: 'Lead', id: r.inquiry_id }); }
      setDirty(false);
    } catch (e) { alert(`Could not save: ${e.message || 'error'}`); }
    finally { setSubmitting(false); }
  };

  // ---- Smart Edit diff + save (same BK-*, canonical PUT) ----
  const changes = useMemo(() => {
    if (!original) return [];
    const o = original;
    const rows = [
      ['Date', (o.event_date || '').slice(0, 10), form.event_date],
      ['Pickup time', o.pickup_time || '', form.pickup_time || ''],
      ['Pickup', o.pickup_location || '', form.pickup_location || ''],
      ['Dropoff', o.dropoff_location || '', form.dropoff_location || ''],
      ['Passengers', String(o.passengers ?? ''), String(form.passengers ?? '')],
      ['Service type', o.service_type || '', form.service_type || ''],
      ['Vehicle', o.vehicle_name || o.vehicle || '', form.vehicle_name || ''],
      ['Hours', String(o.hours ?? ''), isFlat ? '0' : String(form.hours ?? '')],
      ['Notes', (o.notes || '').split(' · Custom price override')[0], form.notes || ''],
      ['Price', money(o.total ?? o.quoted_amount), money(totals.total)],
    ];
    return rows.filter(([, a, b]) => String(a) !== String(b));
  }, [original, form, isFlat, totals.total]);

  const saveEdit = async () => {
    setSubmitting(true); setDiffOpen(false);
    try {
      const path = quoteMode ? `/api/command-center/quote-log/${quoteId}` : `/api/command-center/confirmed-bookings/${bookingId}`;
      const body = quoteMode ? quotePayload() : bookingPayload();
      const r = await fetch(`${API}${path}`, {
        method: 'PUT', credentials: 'include', headers: authHeaders(), body: JSON.stringify(body),
      });
      if (!r.ok) { const t = await r.text().catch(() => ''); throw new Error(t || `PUT ${r.status}`); }
      setDirty(false);
      setDone(quoteMode ? { kind: 'Quote', id: quoteId, edit: true } : { kind: 'Booking', id: bookingId, edit: true });
    } catch (e) { alert(`Could not save: ${e.message || 'error'}`); }
    finally { setSubmitting(false); }
  };

  // ---- Blocked (finalized / not found) ----
  if (blocked) {
    return (
      <div className="os-root min-h-screen flex items-center justify-center px-4" data-testid="smart-edit-blocked">
        <div className="os-elev rounded-2xl p-8 max-w-sm w-full text-center os-animate">
          <h2 className="os-display text-2xl">Can't edit here</h2>
          <p className="text-sm mt-2" style={{ color: 'var(--os-muted)' }}>{blocked.reason}</p>
          <div className="mt-6 space-y-2">
            <button className="os-btn-primary w-full justify-center" data-testid="smart-edit-open-advanced" onClick={() => (blocked.booking?.booking_id ? navigate(`/os/services/${blocked.booking.booking_id}`) : navigate('/os/services?tab=quotes'))}>{blocked.booking?.booking_id ? 'Open Service' : 'Back to Quotes'}</button>
            <button className="os-btn-ghost w-full justify-center" onClick={() => navigate('/os/services')}>Back to Services</button>
          </div>
        </div>
      </div>
    );
  }

  // ---- Success screen ----
  if (done) {
    return (
      <div className="os-root min-h-screen flex items-center justify-center px-4" data-testid="smart-form-success">
        <div className="os-elev rounded-2xl p-8 max-w-sm w-full text-center os-animate">
          <div className="h-14 w-14 rounded-full mx-auto flex items-center justify-center" style={{ background: 'rgba(95,168,113,.16)' }}>
            <Check className="h-7 w-7" style={{ color: 'var(--os-success)' }} />
          </div>
          <h2 className="os-display text-3xl mt-4">{done.edit ? `${done.kind} Updated` : `${done.kind} Created`}</h2>
          <p className="text-sm mt-1 font-mono" style={{ color: 'var(--os-muted)' }} data-testid="smart-form-result-id">{done.id}</p>
          <div className="mt-6 space-y-2">
            <button className="os-btn-primary w-full justify-center" data-testid="smart-form-view-services" onClick={() => navigate(done.kind === 'Quote' ? `/os/services/quotes/${done.id}` : `/os/services?bk=${done.id}`)}>{done.kind === 'Quote' ? 'Open Quote Record' : 'Go to Services'}</button>
            {!done.edit && <button className="os-btn-ghost w-full justify-center" data-testid="smart-form-new-another"
                    onClick={() => { setForm(EMPTY); setPricing(null); setStepIdx(0); setDone(null); setDirty(false); }}>Create Another</button>}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="os-root min-h-screen flex flex-col" data-testid="smart-service-form">
      {/* Top bar */}
      <header className="sticky top-0 z-40 os-surface border-b" style={{ borderColor: 'var(--os-border)' }}>
        <div className="h-16 px-4 flex items-center gap-3 max-w-3xl mx-auto w-full">
          <button onClick={() => requestLeave('/os/services')} className="p-2 rounded-lg" style={{ color: 'var(--os-muted)' }} data-testid="smart-form-close">
            <X className="h-5 w-5" />
          </button>
          <div className="min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-[0.3em]" style={{ color: 'var(--os-red)' }}>{editMode ? `Edit · ${bookingId || quoteId}` : (quoteMode ? 'New Quote' : 'New Service')}</p>
            <h1 className="os-display text-xl leading-none">{editMode ? (quoteMode ? 'EDIT QUOTE' : 'EDIT SERVICE') : (quoteMode ? 'SMART QUOTE' : 'SMART BOOKING')}</h1>
          </div>
          <span className="ml-auto text-xs" style={{ color: 'var(--os-muted)' }}>Step {stepIdx + 1} / {STEPS.length}</span>
        </div>
        {/* Progress rail */}
        <div className="h-1 w-full" style={{ background: 'var(--os-soft)' }}>
          <div style={{ width: `${((stepIdx + 1) / STEPS.length) * 100}%`, height: '100%', background: 'var(--os-red)', transition: 'width .3s ease' }} />
        </div>
      </header>

      {/* Step chips */}
      <div className="max-w-3xl mx-auto w-full px-4 pt-4">
        <div className="flex gap-2 overflow-x-auto os-scroll pb-1" data-testid="smart-form-steps">
          {STEPS.map((s, i) => {
            const Icon = s.icon; const active = i === stepIdx; const complete = i < stepIdx;
            return (
              <button key={s.id} onClick={() => i <= stepIdx && setStepIdx(i)} data-testid={`smart-step-${s.id}`}
                      className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold"
                      style={active ? { background: 'var(--os-red)', color: '#fff' }
                        : complete ? { background: 'rgba(95,168,113,.14)', color: 'var(--os-success)' }
                        : { color: 'var(--os-muted)', border: '1px solid var(--os-border)' }}>
                {complete ? <Check className="h-3.5 w-3.5" /> : <Icon className="h-3.5 w-3.5" />}{s.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Body */}
      <main className="flex-1 max-w-3xl mx-auto w-full px-4 py-5 os-animate" key={step.id}>
        {step.id === 'client' && (
          <div>
            <h2 className="os-display text-2xl mb-4">Who is this for?</h2>
            <Field label="Search existing client">
              <div className="os-input flex items-center gap-2">
                <Search className="h-4 w-4" style={{ color: 'var(--os-muted)' }} />
                <input value={clientQuery} onChange={(e) => setClientQuery(e.target.value)} placeholder="Name, phone or email…"
                       className="bg-transparent outline-none text-sm flex-1" style={{ color: 'var(--os-text)' }} data-testid="smart-client-search" />
              </div>
            </Field>
            {clients.length > 0 && (
              <div className="mb-4 space-y-1.5" data-testid="smart-client-results">
                {clients.map((c) => (
                  <button key={c.key || c.name} className="os-card w-full text-left px-4 py-2.5"
                          data-testid={`smart-client-pick-${c.key || c.name}`}
                          onClick={() => { set({ client_name: c.name || '', client_phone: c.phone || '', client_email: c.email || '' }); setClients([]); setClientQuery(''); }}>
                    <p className="font-semibold text-sm" style={{ color: 'var(--os-text)' }}>{c.name || 'Unnamed'}</p>
                    <p className="text-xs" style={{ color: 'var(--os-muted)' }}>{[c.phone, c.email].filter(Boolean).join(' · ') || '—'}</p>
                  </button>
                ))}
              </div>
            )}
            <Field label="Client name"><input className="os-input" value={form.client_name} onChange={(e) => set({ client_name: e.target.value })} placeholder="Full name" data-testid="smart-client-name" /></Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
              <Field label="Phone"><input className="os-input" value={form.client_phone} onChange={(e) => set({ client_phone: e.target.value })} placeholder="(404) 555-1234" data-testid="smart-client-phone" /></Field>
              <Field label="Email"><input className="os-input" value={form.client_email} onChange={(e) => set({ client_email: e.target.value })} placeholder="name@email.com" data-testid="smart-client-email" /></Field>
            </div>
          </div>
        )}

        {step.id === 'service' && (
          <div>
            <h2 className="os-display text-2xl mb-4">What service?</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {SERVICE_TYPES.map((s) => (
                <button key={s.id} onClick={() => set({ service_type: s.id })} data-testid={`smart-service-${s.id}`}
                        className="os-card px-4 py-3 text-left" style={form.service_type === s.id ? { borderColor: 'var(--os-red)' } : {}}>
                  <p className="font-semibold text-sm" style={{ color: 'var(--os-text)' }}>{s.label}</p>
                  {s.flat && <p className="text-[11px] mt-0.5" style={{ color: 'var(--os-warning)' }}>Flat-fee perimeter pricing</p>}
                </button>
              ))}
            </div>
            {isFlat && (
              <div className="mt-5" data-testid="smart-perimeter">
                <p className="text-[11px] uppercase tracking-wider font-bold mb-2" style={{ color: 'var(--os-muted)' }}>Perimeter</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {PERIMETERS.map((p) => (
                    <button key={p.id} onClick={() => set({ perimeter: p.id })} data-testid={`smart-perimeter-${p.id}`}
                            className="os-card px-4 py-3 text-left" style={form.perimeter === p.id ? { borderColor: 'var(--os-red)' } : {}}>
                      <div className="flex items-center justify-between"><span className="font-semibold text-sm" style={{ color: 'var(--os-text)' }}>{p.label}</span><span className="os-display text-lg" style={{ color: 'var(--os-red)' }}>${p.fee}</span></div>
                      <p className="text-[11px] mt-0.5" style={{ color: 'var(--os-muted)' }}>{p.note}</p>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {step.id === 'when' && (
          <div>
            <h2 className="os-display text-2xl mb-4">When?</h2>
            <Field label="Event date"><input type="date" className="os-input" value={form.event_date} onChange={(e) => set({ event_date: e.target.value })} data-testid="smart-event-date" /></Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
              <Field label="Pickup time"><input type="time" className="os-input" value={form.pickup_time} onChange={(e) => set({ pickup_time: e.target.value })} data-testid="smart-pickup-time" /></Field>
              <Field label="Drop-off time" hint="Optional — helps auto-hours later"><input type="time" className="os-input" value={form.dropoff_time} onChange={(e) => set({ dropoff_time: e.target.value })} data-testid="smart-dropoff-time" /></Field>
            </div>
          </div>
        )}

        {step.id === 'route' && (
          <div>
            <h2 className="os-display text-2xl mb-4">Route</h2>
            <Field label="Pickup location"><input className="os-input" value={form.pickup_location} onChange={(e) => set({ pickup_location: e.target.value })} placeholder="Address / venue" data-testid="smart-pickup-location" /></Field>
            <Field label="Drop-off location"><input className="os-input" value={form.dropoff_location} onChange={(e) => set({ dropoff_location: e.target.value })} placeholder="Address / venue" data-testid="smart-dropoff-location" /></Field>
            {isFlat && (
              <Field label="Flight number" hint="Optional">
                <div className="os-input flex items-center gap-2"><Plane className="h-4 w-4" style={{ color: 'var(--os-muted)' }} />
                  <input value={form.flight_number} onChange={(e) => set({ flight_number: e.target.value })} placeholder="DL1234" className="bg-transparent outline-none text-sm flex-1" style={{ color: 'var(--os-text)' }} data-testid="smart-flight" /></div>
              </Field>
            )}
          </div>
        )}

        {step.id === 'passengers' && (
          <div>
            <h2 className="os-display text-2xl mb-4">Trip details</h2>
            <Field label="Passengers"><input type="number" min="0" className="os-input" value={form.passengers} onChange={(e) => set({ passengers: e.target.value })} placeholder="e.g. 3" data-testid="smart-passengers" /></Field>
            {!isFlat && <Field label="Hours" hint="Minimum hours may apply per vehicle"><input type="number" min="1" step="0.5" className="os-input" value={form.hours} onChange={(e) => set({ hours: e.target.value })} data-testid="smart-hours" /></Field>}
          </div>
        )}

        {step.id === 'vehicle' && (
          <div>
            <h2 className="os-display text-2xl mb-4">Vehicle class</h2>
            {isFlat ? (
              <p className="text-sm" style={{ color: 'var(--os-muted)' }}>Airport transfers use flat-fee pricing; a specific class is optional. Pick one below or continue.</p>
            ) : null}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3" data-testid="smart-vehicle-list">
              {Object.entries(rateCard).map(([key, v]) => (
                <button key={key} onClick={() => set({ vehicle_type: key, vehicle_name: v.name })} data-testid={`smart-vehicle-${key}`}
                        className="os-card px-4 py-3 text-left" style={form.vehicle_type === key ? { borderColor: 'var(--os-red)' } : {}}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold text-sm truncate" style={{ color: 'var(--os-text)' }}>{v.name}</span>
                    <span className="os-display text-base shrink-0" style={{ color: 'var(--os-red)' }}>${v.rate}/hr</span>
                  </div>
                  <p className="text-[11px] mt-0.5" style={{ color: 'var(--os-muted)' }}>{v.pax ? `${v.pax} pax · ` : ''}min {v.min_hours}h{v.exotic ? ' · exotic' : ''}</p>
                </button>
              ))}
            </div>
            <div className="mt-5">
              <p className="text-[11px] uppercase tracking-wider font-bold mb-2" style={{ color: 'var(--os-muted)' }}>Pricing tier</p>
              <div className="inline-flex rounded-lg p-0.5" style={{ background: 'var(--os-soft)', border: '1px solid var(--os-border)' }}>
                {['retail', 'member'].map((t) => (
                  <button key={t} onClick={() => set({ pricing_type: t })} data-testid={`smart-pricing-${t}`}
                          className="px-4 h-9 rounded-md text-sm font-semibold capitalize"
                          style={form.pricing_type === t ? { background: 'var(--os-red)', color: '#fff' } : { color: 'var(--os-muted)' }}>{t}</button>
                ))}
              </div>
            </div>
          </div>
        )}

        {step.id === 'price' && (
          <div>
            <h2 className="os-display text-2xl mb-4">Authoritative price</h2>
            {pricingLoading ? (
              <div className="os-card p-8 flex items-center justify-center gap-2" style={{ color: 'var(--os-muted)' }} data-testid="smart-price-loading"><Loader2 className="h-5 w-5 animate-spin" /> Calculating…</div>
            ) : pricingErr ? (
              <div className="os-card p-6 text-center" data-testid="smart-price-error">
                <p className="text-sm" style={{ color: 'var(--os-danger)' }}>Pricing engine is unavailable for this selection.</p>
                <button className="os-btn-ghost mt-3" onClick={runPricing} data-testid="smart-price-retry">Retry</button>
              </div>
            ) : pricing ? (
              <div className="os-card p-5" data-testid="smart-price-card">
                <div className="flex items-center justify-between"><span className="text-sm" style={{ color: 'var(--os-muted)' }}>{pricing.vehicle || form.vehicle_name || form.service_type}</span>
                  <StatusBadge status="ready" label={pricing.flat ? 'Flat fee' : `${pricing.effective_hours}h × ${money(pricing.applied_rate)}`} /></div>
                <div className="mt-4 space-y-2 text-sm">
                  <Row label={pricing.flat ? 'Flat base' : 'Base'} value={money(totals.base)} />
                  <Row label={`Gratuity (${gratPct}%)`} value={money(totals.gratuity)} muted />
                  <Row label={`Sales tax (${taxPct}%)`} value={money(totals.sales_tax)} muted />
                  <div className="border-t pt-2 mt-2" style={{ borderColor: 'var(--os-border)' }}>
                    <Row label="Total" value={money(totals.total)} bold />
                  </div>
                </div>
                <label className="flex items-center gap-2 mt-4 cursor-pointer">
                  <input type="checkbox" checked={form.override_price} onChange={(e) => set({ override_price: e.target.checked })} data-testid="smart-override-toggle" />
                  <span className="text-xs" style={{ color: 'var(--os-muted)' }}>Custom price override (admin)</span>
                </label>
                {form.override_price && (
                  <input type="number" className="os-input mt-2" value={form.override_total} onChange={(e) => set({ override_total: e.target.value })} placeholder="Final total" data-testid="smart-override-input" />
                )}
              </div>
            ) : (
              <p className="text-sm" style={{ color: 'var(--os-muted)' }}>Select a vehicle first to calculate price.</p>
            )}
          </div>
        )}

        {step.id === 'review' && (
          <div>
            <h2 className="os-display text-2xl mb-4">Review & create</h2>
            <div className="os-card p-5 space-y-3" data-testid="smart-review-summary">
              <SummaryRow label="Client" value={form.client_name || form.client_phone || form.client_email || '—'} />
              <SummaryRow label="Service" value={isFlat ? `${form.service_type} · ${PERIMETERS.find((p) => p.id === form.perimeter)?.label}` : form.service_type} />
              <SummaryRow label="When" value={[form.event_date, form.pickup_time].filter(Boolean).join(' · ') || '—'} />
              <SummaryRow label="Route" value={[form.pickup_location, form.dropoff_location].filter(Boolean).join(' → ') || '—'} />
              <SummaryRow label="Passengers / hours" value={`${form.passengers || '—'} pax${isFlat ? '' : ` · ${form.hours}h`}`} />
              <SummaryRow label="Vehicle" value={form.vehicle_name || '—'} />
              <SummaryRow label="Total" value={money(totals.total)} bold />
            </div>
            <Field label="Notes"><textarea className="os-input" rows={2} value={form.notes} onChange={(e) => set({ notes: e.target.value })} placeholder="Special requests, itinerary…" data-testid="smart-notes" /></Field>
            {editMode ? (
              <div className="mt-4">
                <button className="os-btn-primary w-full justify-center" disabled={submitting} onClick={() => setDiffOpen(true)} data-testid="smart-edit-review">
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Review Changes & Save
                </button>
                {!changes.length && <p className="text-[11px] mt-2 text-center" style={{ color: 'var(--os-muted)' }}>No changes yet.</p>}
              </div>
            ) : quoteMode ? (
              <div className="mt-4">
                <button className="os-btn-primary w-full justify-center" disabled={submitting} onClick={() => submit('quote')} data-testid="smart-submit-quote">
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Create Quote (QT)
                </button>
              </div>
            ) : (
            <div className="mt-4 space-y-2">
              <button className="os-btn-primary w-full justify-center" disabled={submitting} onClick={() => submit('booking')} data-testid="smart-submit-booking">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Create Booking (BK)
              </button>
              <div className="grid grid-cols-2 gap-2">
                <button className="os-btn-ghost justify-center" disabled={submitting} onClick={() => submit('quote')} data-testid="smart-submit-quote">Save as Quote (QT)</button>
                <button className="os-btn-ghost justify-center" disabled={submitting} onClick={() => submit('lead')} data-testid="smart-submit-lead">Save as Lead (INQ)</button>
              </div>
            </div>
            )}
          </div>
        )}
      </main>

      {/* Sticky footer nav */}
      {step.id !== 'review' && (
        <footer className="sticky bottom-0 os-surface border-t" style={{ borderColor: 'var(--os-border)', paddingBottom: 'calc(env(safe-area-inset-bottom) + 12px)' }} data-testid="smart-form-footer">
          <div className="max-w-3xl mx-auto w-full px-4 py-3 flex items-center gap-2">
            {stepIdx > 0 && <button className="os-btn-ghost" onClick={back} data-testid="smart-back"><ArrowLeft className="h-4 w-4" /> Back</button>}
            <div className="flex-1" />
            <button className="os-btn-primary" disabled={!stepValid} style={!stepValid ? { opacity: 0.5 } : {}} onClick={next} data-testid="smart-next">Continue <ArrowRight className="h-4 w-4" /></button>
          </div>
        </footer>
      )}

      <ConfirmModal open={leaveOpen} title="Discard this booking?" body="Your progress isn't saved yet and will be lost."
        confirmLabel="Discard" danger onConfirm={() => { setDirty(false); setLeaveOpen(false); navigate(pendingLeave || '/os/services'); }}
        onClose={() => setLeaveOpen(false)} testid="smart-leave-confirm" />

      <ConfirmModal open={diffOpen} title={changes.length ? 'Save these changes?' : 'No changes'}
        body={changes.length
          ? <span data-testid="smart-edit-diff">{changes.map(([l, a, b], i) => <React.Fragment key={i}><span style={{ color: 'var(--os-text)' }}>{l}:</span> {a || '—'} → <span style={{ color: 'var(--os-red)' }}>{b || '—'}</span>{i < changes.length - 1 && <br />}</React.Fragment>)}</span>
          : 'Nothing was modified on this booking.'}
        confirmLabel={changes.length ? 'Save Changes' : 'OK'}
        onConfirm={() => (changes.length ? saveEdit() : setDiffOpen(false))}
        onClose={() => setDiffOpen(false)} testid="smart-edit-modal" confirmTestid="smart-edit-confirm" />
    </div>
  );
}

const Row = ({ label, value, muted, bold }) => (
  <div className="flex items-center justify-between">
    <span style={{ color: muted ? 'var(--os-muted)' : 'var(--os-text)' }}>{label}</span>
    <span className={bold ? 'os-display text-xl' : ''} style={{ color: bold ? 'var(--os-red)' : 'var(--os-text)', fontWeight: bold ? undefined : 600 }}>{value}</span>
  </div>
);
const SummaryRow = ({ label, value, bold }) => (
  <div className="flex items-start justify-between gap-3">
    <span className="text-[11px] uppercase tracking-wider font-bold shrink-0" style={{ color: 'var(--os-muted)' }}>{label}</span>
    <span className="text-sm text-right" style={{ color: bold ? 'var(--os-red)' : 'var(--os-text)', fontWeight: bold ? 800 : 500 }}>{value}</span>
  </div>
);
